import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';

const STATUS_COLORS = { draft: 'badge-gray', approved: 'badge-green', rejected: 'badge-red', sent: 'badge-blue', pending: 'badge-yellow', paid: 'badge-green', active: 'badge-green', assigned: 'badge-blue', received: 'badge-green' };

export default function Procurement() {
  const [tab, setTab] = useState('requisitions');

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        {[
          { key: 'requisitions', label: 'Purchase Requisitions' },
          { key: 'orders', label: 'Purchase Orders' },
          { key: 'receipts', label: 'Goods Receipts' },
          { key: 'invoices', label: 'Vendor Invoices' },
          { key: 'assets', label: 'Assets' },
        ].map(t => (
          <button key={t.key} className={`tab-btn ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {tab === 'requisitions' && <RequisitionsTab />}
      {tab === 'orders' && <OrdersTab />}
      {tab === 'receipts' && <ReceiptsTab />}
      {tab === 'invoices' && <InvoicesTab />}
      {tab === 'assets' && <AssetsTab />}
    </div>
  );
}

function RequisitionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ description: '', estimated_amount: '' });

  const fetch_ = useCallback(async () => {
    try {
      const res = await apiFetch('/api/procurement/purchase-requisitions');
      if (res.ok) { const d = await res.json(); setItems(d.requisitions || []); }
    } catch (e) { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  async function handleCreate() {
    const res = await apiFetch('/api/procurement/purchase-requisitions', { method: 'POST', body: JSON.stringify(form) });
    if (res.ok) { setModalOpen(false); setForm({ description: '', estimated_amount: '' }); fetch_(); }
  }

  async function handleApprove(id) { await apiFetch(`/api/procurement/purchase-requisitions/${id}/approve`, { method: 'POST' }); fetch_(); }
  async function handleReject(id) {
    const reason = prompt('Rejection reason:');
    if (reason !== null) { await apiFetch(`/api/procurement/purchase-requisitions/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }); fetch_(); }
  }

  return (
    <div className="card">
      <div className="card-header"><h4>Purchase Requisitions</h4><button className="btn btn-primary btn-sm" onClick={() => setModalOpen(true)}>+ New PR</button></div>
      <table className="data-table">
        <thead><tr><th>PR #</th><th>Description</th><th>Amount</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>
          {loading ? <tr><td colSpan={5} style={{ textAlign: 'center' }}>Loading...</td></tr> : items.length === 0 ? <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8' }}>No requisitions</td></tr> : items.map(item => (
            <tr key={item.id}>
              <td style={{ fontWeight: 500 }}>{item.pr_number}</td>
              <td>{item.description || '-'}</td>
              <td>{parseFloat(item.estimated_amount || 0).toLocaleString()}</td>
              <td><span className={`badge ${STATUS_COLORS[item.status] || 'badge-gray'}`}>{item.status}</span></td>
              <td>
                {item.status === 'draft' && <>
                  <button className="btn btn-sm btn-success" onClick={() => handleApprove(item.id)} style={{ marginRight: 4 }}>Approve</button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleReject(item.id)}>Reject</button>
                </>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header"><h3>New Purchase Requisition</h3><button className="modal-close" onClick={() => setModalOpen(false)}>X</button></div>
            <div className="modal-body">
              <div className="form-group"><label>Description *</label><textarea className="form-control" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              <div className="form-group"><label>Estimated Amount</label><input type="number" className="form-control" value={form.estimated_amount} onChange={e => setForm({ ...form, estimated_amount: e.target.value })} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={!form.description}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OrdersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ vendor_id: '', order_date: '', delivery_date: '', terms: '' });

  const fetch_ = useCallback(async () => {
    try {
      const [posRes, vendorsRes] = await Promise.all([
        apiFetch('/api/procurement/purchase-orders'),
        apiFetch('/api/vendors?limit=500')
      ]);
      if (posRes.ok) { const d = await posRes.json(); setItems(d.purchaseOrders || []); }
      if (vendorsRes.ok) { const d = await vendorsRes.json(); setVendors(d.vendors || []); }
    } catch (e) { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  async function handleCreate() {
    const res = await apiFetch('/api/procurement/purchase-orders', { method: 'POST', body: JSON.stringify(form) });
    if (res.ok) { setModalOpen(false); setForm({ vendor_id: '', order_date: '', delivery_date: '', terms: '' }); fetch_(); }
  }

  async function handleApprove(id) { await apiFetch(`/api/procurement/purchase-orders/${id}/approve`, { method: 'POST' }); fetch_(); }
  async function handleSend(id) { await apiFetch(`/api/procurement/purchase-orders/${id}/send`, { method: 'POST' }); fetch_(); }

  return (
    <div className="card">
      <div className="card-header"><h4>Purchase Orders</h4><button className="btn btn-primary btn-sm" onClick={() => setModalOpen(true)}>+ New PO</button></div>
      <table className="data-table">
        <thead><tr><th>PO #</th><th>Vendor</th><th>Date</th><th>Amount</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>
          {loading ? <tr><td colSpan={6} style={{ textAlign: 'center' }}>Loading...</td></tr> : items.length === 0 ? <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8' }}>No purchase orders</td></tr> : items.map(item => (
            <tr key={item.id}>
              <td style={{ fontWeight: 500 }}>{item.po_number}</td>
              <td>{item.vendor_name || '-'}</td>
              <td>{item.order_date || '-'}</td>
              <td>{parseFloat(item.total_amount || 0).toLocaleString()}</td>
              <td><span className={`badge ${STATUS_COLORS[item.status] || 'badge-gray'}`}>{item.status}</span></td>
              <td>
                {item.status === 'draft' && <button className="btn btn-sm btn-success" onClick={() => handleApprove(item.id)} style={{ marginRight: 4 }}>Approve</button>}
                {item.status === 'approved' && <button className="btn btn-sm btn-primary" onClick={() => handleSend(item.id)}>Send</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header"><h3>New Purchase Order</h3><button className="modal-close" onClick={() => setModalOpen(false)}>X</button></div>
            <div className="modal-body">
              <div className="form-group"><label>Vendor *</label>
                <select className="form-control" value={form.vendor_id} onChange={e => setForm({ ...form, vendor_id: e.target.value })}>
                  <option value="">Select Vendor</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Order Date</label><input type="date" className="form-control" value={form.order_date} onChange={e => setForm({ ...form, order_date: e.target.value })} /></div>
                <div className="form-group"><label>Delivery Date</label><input type="date" className="form-control" value={form.delivery_date} onChange={e => setForm({ ...form, delivery_date: e.target.value })} /></div>
              </div>
              <div className="form-group"><label>Terms</label><textarea className="form-control" rows={2} value={form.terms} onChange={e => setForm({ ...form, terms: e.target.value })} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={!form.vendor_id}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReceiptsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/procurement/goods-receipts');
        if (res.ok) { const d = await res.json(); setItems(d.receipts || []); }
      } catch (e) { /* */ }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="card">
      <div className="card-header"><h4>Goods/Service Receipts</h4></div>
      <table className="data-table">
        <thead><tr><th>Receipt #</th><th>PO #</th><th>Date</th><th>Status</th></tr></thead>
        <tbody>
          {loading ? <tr><td colSpan={4} style={{ textAlign: 'center' }}>Loading...</td></tr> : items.length === 0 ? <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8' }}>No receipts</td></tr> : items.map(item => (
            <tr key={item.id}>
              <td>{item.receipt_number}</td><td>{item.po_number || '-'}</td><td>{item.receipt_date || '-'}</td>
              <td><span className={`badge ${STATUS_COLORS[item.status] || 'badge-gray'}`}>{item.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvoicesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch_ = useCallback(async () => {
    try {
      const res = await apiFetch('/api/procurement/vendor-invoices');
      if (res.ok) { const d = await res.json(); setItems(d.invoices || []); }
    } catch (e) { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  async function handleMatch(id) {
    const res = await apiFetch(`/api/procurement/vendor-invoices/${id}/match`, { method: 'POST' });
    if (res.ok) { const d = await res.json(); alert(d.matched ? '3-way match successful' : '3-way match failed - check PO and receipt'); fetch_(); }
  }

  return (
    <div className="card">
      <div className="card-header"><h4>Vendor Invoices</h4></div>
      <table className="data-table">
        <thead><tr><th>Invoice #</th><th>Vendor</th><th>Date</th><th>Total</th><th>3-Way Match</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>
          {loading ? <tr><td colSpan={7} style={{ textAlign: 'center' }}>Loading...</td></tr> : items.length === 0 ? <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8' }}>No invoices</td></tr> : items.map(item => (
            <tr key={item.id}>
              <td>{item.invoice_number}</td><td>{item.vendor_name || '-'}</td><td>{item.invoice_date || '-'}</td>
              <td>{parseFloat(item.total || 0).toLocaleString()}</td>
              <td><span className={`badge ${item.three_way_match ? 'badge-green' : 'badge-yellow'}`}>{item.three_way_match ? 'Matched' : 'Pending'}</span></td>
              <td><span className={`badge ${STATUS_COLORS[item.status] || 'badge-gray'}`}>{item.status}</span></td>
              <td>{!item.three_way_match && <button className="btn btn-sm btn-primary" onClick={() => handleMatch(item.id)}>Match</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AssetsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: '', category: '', serial_number: '', cost: '', useful_life_months: '' });

  const fetch_ = useCallback(async () => {
    try {
      const res = await apiFetch('/api/procurement/assets');
      if (res.ok) { const d = await res.json(); setItems(d.assets || []); }
    } catch (e) { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  async function handleCreate() {
    const res = await apiFetch('/api/procurement/assets', { method: 'POST', body: JSON.stringify(form) });
    if (res.ok) { setModalOpen(false); setForm({ name: '', category: '', serial_number: '', cost: '', useful_life_months: '' }); fetch_(); }
  }

  async function handleReturn(id) { await apiFetch(`/api/procurement/assets/${id}/return`, { method: 'POST' }); fetch_(); }

  return (
    <div className="card">
      <div className="card-header"><h4>Asset Register</h4><button className="btn btn-primary btn-sm" onClick={() => setModalOpen(true)}>+ New Asset</button></div>
      <table className="data-table">
        <thead><tr><th>Name</th><th>Category</th><th>Serial #</th><th>Cost</th><th>Current Value</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>
          {loading ? <tr><td colSpan={7} style={{ textAlign: 'center' }}>Loading...</td></tr> : items.length === 0 ? <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8' }}>No assets</td></tr> : items.map(item => (
            <tr key={item.id}>
              <td style={{ fontWeight: 500 }}>{item.name}</td>
              <td>{item.category || '-'}</td>
              <td>{item.serial_number || '-'}</td>
              <td>{parseFloat(item.cost || 0).toLocaleString()}</td>
              <td>{parseFloat(item.current_value || 0).toLocaleString()}</td>
              <td><span className={`badge ${STATUS_COLORS[item.status] || 'badge-gray'}`}>{item.status}</span></td>
              <td>{item.status === 'assigned' && <button className="btn btn-sm btn-secondary" onClick={() => handleReturn(item.id)}>Return</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header"><h3>New Asset</h3><button className="modal-close" onClick={() => setModalOpen(false)}>X</button></div>
            <div className="modal-body">
              <div className="form-group"><label>Name *</label><input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Category</label><input className="form-control" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} /></div>
                <div className="form-group"><label>Serial Number</label><input className="form-control" value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })} /></div>
                <div className="form-group"><label>Cost</label><input type="number" className="form-control" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} /></div>
                <div className="form-group"><label>Useful Life (months)</label><input type="number" className="form-control" value={form.useful_life_months} onChange={e => setForm({ ...form, useful_life_months: e.target.value })} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={!form.name}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
